/**
 * The routing deck: what Kimene does, in one picture.
 *
 * A direct descendant of the console's own `DispatchPlate` (the sign-in screen's
 * 480×530 plate), grown into five stages — pools, the eligibility gate, the
 * weight lanes, the decision, and the route with its record. The console's plate
 * draws the *act*; this one draws the act with its evidence attached, because
 * that is the claim the page is making.
 *
 * Two things it does that a marketing diagram normally does not:
 *
 *   **The refusals carry their real codes.** Seven of twelve candidates are
 *   struck out, and each one prints the constant from
 *   `internal/domain/routing.go` with the console's own Turkish beneath it.
 *   Routing is mostly refusal; a picture that showed only the winner would be
 *   showing the least interesting part.
 *
 *   **The weights are the shipped table.** Six components and four penalties
 *   from `internal/routing/weights.go`, guarded by `data/routing.test.ts`.
 *
 * Reduced motion is not a degraded version of this figure — it is this figure,
 * at rest. Every element declares its final position in the markup; the timeline
 * only removes and restores an entrance offset. Under reduced motion nothing
 * runs and nothing is missing: the crosses are stamped, the codes are readable,
 * the lanes are full, the score reads 0.78, the route is drawn and the token is
 * parked a third of the way along it — visibly in transit, which is the thing
 * the movement was saying, said without movement.
 */
import { m } from 'motion/react'

import { useReducedMotionSafe } from '../../kit'
import { useT, useLocale } from '../../i18n/context'
import { COMPONENTS, PENALTIES } from '../../data/routing'
import { CANDIDATES, DECISION, REJECTION_TR, REJECTION_EN } from '../../data/fixtures'
import './HeroDeck.css'

/*
 * The deck's geometry. One table, so the five stages cannot drift apart.
 *
 * `LANE_X` is 720 rather than 640 for a measured reason: the longest rejection
 * constant is `ACCOUNT_NOT_REACHABLE_FROM_HOST`, and at this figure's mono size
 * it runs to about x=630 from the gate. At 640 it printed *through* the weight
 * labels — two true things overlapping into one unreadable one. Any new code
 * longer than that needs this number moved, which is why it is written down
 * here rather than tuned by eye.
 */
const W = 1360
const H = 545
const POOL_X = 40
const POOL_W = 210
const GATE_X = 430
const LANE_X = 720
const LANE_W = 250
const NODE_X = 1080
const NODE_Y = 270
const REC_X = 1150
/*
 * The corridor the survivors travel in, chosen so it crosses no text at all.
 *
 * Three constraints, and every one was found by drawing the line and looking at
 * what it went through:
 *   · the drop happens between DROP_X and BUS_X — right of the longest rejection
 *     code (which ends near x=630) and left of the weight lanes (LANE_X);
 *   · the run happens at BUS_Y, below the penalty block, which ends near y=404;
 *   · the climb happens right of the lanes (LANE_X + LANE_W = 970).
 * A figure arguing that its numbers are real cannot have a decorative curve
 * drawn through them.
 */
const BUS_Y = 500
const DROP_X = 660
const BUS_X = 700
const CLIMB_X = 1020

/** Where a candidate's row sits. Twelve rows over the gate's height. */
const rowY = (i: number) => 96 + i * 34

export function HeroDeck() {
  const t = useT()
  const locale = useLocale()
  const reduced = useReducedMotionSafe()
  const reject = locale === 'tr' ? REJECTION_TR : REJECTION_EN

  /*
   * Entrance timings, in seconds — and the entrance is a *slide*, not a fade.
   *
   * This started as `initial={{ opacity: 0 }}`, which is the obvious way to
   * stage a five-part figure and is the exact defect the whole kit is built to
   * prevent. Two things go wrong and neither announces itself:
   *
   *   · `motion` writes inline styles, so the prerendered HTML shipped every
   *     rejection code, every weight and the decision itself at `opacity: 0`.
   *     A crawler, a link preview and a reader with JavaScript off got a figure
   *     with nothing in it — while the `prefers-reduced-motion` reset in
   *     base.css, which only reaches CSS, sat there looking like it covered this.
   *   · With delays running to 2.7 s, anyone screenshotting or scrolling past
   *     early saw the same empty figure. That is how it was caught.
   *
   * So `opacity` is never touched. The offset is horizontal, which also reads
   * better here: the deck argues left-to-right, and each stage arriving from the
   * one before it is the argument.
   */
  const at = (ms: number) => (reduced ? 0 : ms / 1000)
  const enter = (delay: number) =>
    reduced
      ? {}
      : {
          initial: { x: -10 },
          animate: { x: 0 },
          transition: { duration: 0.3, delay, ease: [0.32, 0.72, 0, 1] as const },
        }

  return (
    <figure className="kl-deck" role="img" aria-label={t('hero.fig.alt')}>
      <svg
        className="kl-deck__svg"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        focusable="false"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* ---- stage captions ---- */}
        <text className="kl-deck__cap" x={POOL_X} y={64}>
          {t('hero.fig.pools')}
        </text>
        <text className="kl-deck__cap" x={GATE_X - 6} y={64}>
          {t('hero.fig.eligibility')}
        </text>
        <text className="kl-deck__cap" x={LANE_X} y={64}>
          {t('hero.fig.weights')}
        </text>
        <text className="kl-deck__cap" x={NODE_X - 40} y={64}>
          {t('hero.fig.decision')}
        </text>
        <text className="kl-deck__cap" x={NODE_X + 40} y={378}>
          {t('hero.fig.route')}
        </text>
        <text className="kl-deck__cap" x={REC_X} y={64}>
          {t('hero.fig.record')}
        </text>

        {/* ---- A · the candidates arriving from the pools ---- */}
        {CANDIDATES.map((c, i) => {
          const y = rowY(i)
          return (
            <m.g key={c.id} {...enter(at(60 + i * 24))}>
              <rect
                className={`kl-deck__chip${c.reject ? '' : ' kl-deck__chip--live'}`}
                x={POOL_X}
                y={y - 9}
                width={POOL_W}
                height={18}
                rx={3}
              />
              <text className="kl-deck__chiptext" x={POOL_X + 8} y={y + 4}>
                {c.account}
              </text>
              <text className="kl-deck__chipdim" x={POOL_X + POOL_W - 8} y={y + 4} textAnchor="end">
                {c.host}
              </text>
              {/* the line to the gate */}
              <line
                className={`kl-deck__wire${c.reject ? '' : ' kl-deck__wire--live'}`}
                x1={POOL_X + POOL_W}
                y1={y}
                x2={GATE_X}
                y2={y}
              />
            </m.g>
          )
        })}

        {/* ---- B · the gate ---- */}
        <line className="kl-deck__gate" x1={GATE_X} y1={78} x2={GATE_X} y2={rowY(11) + 26} />

        {/*
          The five that got through, bundled and carried under the scoring block.
          Without them the middle of the deck was a blank band: twelve wires ran
          into the gate, seven were struck out, and the survivors simply stopped,
          which read as "nothing happens to the ones that pass".

          They gather onto one bus below the lanes rather than fanning straight
          across. Five independent curves drawn edge-to-edge crossed the penalty
          labels — `rate_limit_proximity` had a wire through the middle of it —
          and a figure whose whole point is that the numbers are real cannot have
          decoration drawn over them.
        */}
        {CANDIDATES.map((c, i) => {
          if (c.reject) return null
          const y = rowY(i)
          return (
            <g key={`s-${c.id}`}>
              {/* The counterpart to the cross: the gate marks what passed as
                  plainly as what it turned away. */}
              <path className="kl-deck__tick" d={`M${GATE_X + 6} ${y} l 4 5 l 9 -11`} />
              <path
                className="kl-deck__survivor"
                d={`M${GATE_X} ${y} H ${DROP_X} C ${DROP_X + 24} ${y}, ${BUS_X - 10} ${BUS_Y}, ${BUS_X} ${BUS_Y}`}
              />
            </g>
          )
        })}
        <path
          className="kl-deck__survivor kl-deck__survivor--bus"
          d={`M${BUS_X} ${BUS_Y} H ${CLIMB_X} C ${NODE_X} ${BUS_Y}, ${NODE_X} ${NODE_Y + 80}, ${NODE_X} ${NODE_Y + 34}`}
        />

        {CANDIDATES.map((c, i) => {
          if (!c.reject) return null
          const y = rowY(i)
          return (
            <m.g key={`r-${c.id}`} {...enter(at(420 + i * 30))}>
              {/* The refusal mark. A cross, not a fade: a refusal is a decision,
                  and it is drawn as firmly as the route is. */}
              <path
                className="kl-deck__cross"
                d={`M${GATE_X + 6} ${y - 6} L${GATE_X + 18} ${y + 6} M${GATE_X + 18} ${y - 6} L${GATE_X + 6} ${y + 6}`}
              />
              <text className="kl-deck__code" x={GATE_X + 26} y={y - 1}>
                {c.reject}
              </text>
              <text className="kl-deck__reason" x={GATE_X + 26} y={y + 10}>
                {reject[c.reject]}
              </text>
            </m.g>
          )
        })}

        {/* ---- C · the weight lanes ---- */}
        {COMPONENTS.map((c, i) => {
          const y = 120 + i * 30
          const w = LANE_W * (c.weight / 0.25)
          return (
            <m.g key={c.key} {...enter(at(820 + i * 34))}>
              <text className="kl-deck__lane" x={LANE_X} y={y - 6}>
                {c.key}
              </text>
              <text className="kl-deck__laneval" x={LANE_X + LANE_W} y={y - 6} textAnchor="end">
                {c.weight.toFixed(2)}
              </text>
              <rect className="kl-deck__track" x={LANE_X} y={y} width={LANE_W} height={6} rx={3} />
              <m.rect
                className="kl-deck__fill"
                x={LANE_X}
                y={y}
                height={6}
                rx={3}
                initial={reduced ? undefined : { width: 0 }}
                animate={reduced ? undefined : { width: w }}
                width={reduced ? w : undefined}
                transition={{ duration: 0.42, delay: at(860 + i * 34), ease: [0.32, 0.72, 0, 1] }}
              />
            </m.g>
          )
        })}

        {/* The penalties, growing the other way. One of them is the unmeasured
            lane: hollow, dashed and violet, because an unmeasured input is not a
            small input — it is a different kind of thing. */}
        <text className="kl-deck__lane kl-deck__lane--penalty" x={LANE_X} y={318}>
          {t('ui.penalties')}
        </text>
        {PENALTIES.map((p, i) => {
          const y = 332 + i * 24
          const w = LANE_W * (p.weight / 0.25)
          const unknown = p.key === 'unknown_capacity'
          return (
            <m.g key={p.key} {...enter(at(1060 + i * 30))}>
              <text className="kl-deck__penkey" x={LANE_X} y={y + 8}>
                {p.key}
              </text>
              <rect
                className={unknown ? 'kl-deck__unmeasured' : 'kl-deck__penalty'}
                x={LANE_X + LANE_W - w}
                y={y}
                width={w}
                height={6}
                rx={3}
              />
              <text className="kl-deck__laneval" x={LANE_X + LANE_W + 8} y={y + 7}>
                −{p.weight.toFixed(2)}
              </text>
            </m.g>
          )
        })}

        {/* ---- D · the decision ---- */}
        <m.g {...enter(at(1220))}>
          <circle className="kl-deck__halo" cx={NODE_X} cy={NODE_Y} r={34} />
          <rect
            className="kl-deck__node"
            x={NODE_X - 13}
            y={NODE_Y - 13}
            width={26}
            height={26}
            rx={3}
            transform={`rotate(45 ${NODE_X} ${NODE_Y})`}
          />
          {/*
            Above the node, not below it. Below is where the survivors' bus
            climbs in, and the curve ran straight through `0.86 − 0.08` — the one
            piece of arithmetic the figure exists to show. Above the node the
            space between the stage caption and the diamond was empty anyway.
          */}
          <text className="kl-deck__score" x={NODE_X} y={NODE_Y - 62} textAnchor="middle">
            {DECISION.total.toFixed(2)}
          </text>
          <text className="kl-deck__sum" x={NODE_X} y={NODE_Y - 44} textAnchor="middle">
            {DECISION.componentTotal.toFixed(2)} − {DECISION.penaltyTotal.toFixed(2)}
          </text>
        </m.g>

        {/* ---- E · the route, and the record it writes ---- */}
        <path className="kl-deck__route" d={ROUTE} />
        <path className={`kl-deck__token${reduced ? ' kl-deck__token--parked' : ''}`} d={ROUTE} />

        <m.g {...enter(at(1420))}>
          {[0, 1, 2].map((i) => (
            <rect
              key={i}
              className="kl-deck__record"
              x={REC_X}
              y={398 + i * 34}
              width={170}
              height={26}
              rx={3}
            />
          ))}
          <text className="kl-deck__recid" x={REC_X + 8} y={415}>
            {DECISION.id}
          </text>
          <text className="kl-deck__recnote" x={REC_X + 8} y={449}>
            {t('ui.immutable')}
          </text>
          <text className="kl-deck__recnote" x={REC_X + 8} y={483}>
            {CANDIDATES.filter((c) => c.reject).length} {t('ui.rejected')}
          </text>
        </m.g>
      </svg>
    </figure>
  )
}

/** One string, two paths: the route, and the bright dash that runs it. */
const ROUTE = `M${NODE_X + 24} ${NODE_Y} C ${NODE_X + 80} ${NODE_Y}, ${REC_X - 50} 350, ${REC_X + 40} 392`
